import React, { createContext, useReducer } from 'react';
import client from './apollo';
import api from './common/api';
import {
  IS_TWO_FA_ENABLED,
  REFRESH_TOKEN,
  TOPIC_ID,
  TOKEN,
  TWO_FA_KEY,
  USER,
} from './common/constants';

const getLoggedInUser = () => {
  // eslint-disable-next-line no-undef
  let loggedInUser = localStorage?.getItem(USER);
  loggedInUser = loggedInUser ? JSON?.parse(loggedInUser) : null;

  return loggedInUser;
};

const initialState = {
  currentUser: getLoggedInUser() || {},
  // eslint-disable-next-line no-undef
  authToken: localStorage?.getItem(TOKEN),
  showPrompt: false,
  topicsId:null,
};

const reducer = (state, action) => {
  switch (action?.type) {
    case 'SET_CURRENT_USER':
      // eslint-disable-next-line no-case-declarations
      const user = action?.data || {};

      // eslint-disable-next-line no-undef
      localStorage.setItem(
        USER,
        user && Object?.keys(user)?.length ? JSON?.stringify(user) : null,
      );
      return { ...state, currentUser: { ...user } };
    case 'SET_CURRENT_ROLE':
      return { ...state, currentRole: action?.data };
    case 'LOGOUT':
      delete api?.defaults?.headers?.common?.Authorization;
      // eslint-disable-next-line no-undef
      localStorage?.clear();
      client?.clearStore();
      return {
        ...initialState,
        authenticated: false,
        authToken: null,
        user: {},
      };
    case 'SET_FETCHING_USER_DETAILS':
      return { ...state, fetchingUserDetails: action?.data };
    case 'SET_AUTHENTICATED':
      return { ...state, authenticated: action?.data };
    case 'SET_TOKEN':
      // eslint-disable-next-line no-undef
      localStorage.setItem(TOKEN, action?.data);
      return { ...state, authToken: action?.data };
    case 'SET_REFRESH_TOKEN':
      // eslint-disable-next-line no-undef
      localStorage.setItem(REFRESH_TOKEN, action?.data);
      return {
        ...state,
        refreshToken: action?.data,
      };
    case 'SET_TWO_FA_KEY':
      // eslint-disable-next-line no-case-declarations
      const twoFaKey = action?.data || {};

      // eslint-disable-next-line no-undef
      localStorage.setItem(
        TWO_FA_KEY,
        twoFaKey && Object?.keys(twoFaKey)?.length
          ? JSON?.stringify(twoFaKey)
          : null,
      );
      return { ...state, twoFaKey: { ...twoFaKey } };
    case 'SET_TWO_FA_VERIFIED':
      // eslint-disable-next-line no-undef
      localStorage?.setItem(IS_TWO_FA_ENABLED, action?.data);
      return { ...state, isTwoFaEnabled: action?.data };
    case 'SET_TOPIC_ID' :
      // eslint-disable-next-line no-undef
      localStorage?.setItem(TOPIC_ID, action?.data);
      return { ...state, topicsId: action?.data };
    case 'SET_INITIAL_SHOW_ALL_FIELDS_STATUS':
      return { ...state, initialShowAllFieldStatus: action?.data };
    default:
      return { ...state };
  }
};

const AppContext = createContext({
  state: initialState,
  dispatch: () => {},
});

function AppContextProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getToken = () =>
    // eslint-disable-next-line no-undef
    localStorage?.getItem(TOKEN) || null;
  const getRefreshToken = () =>
    // eslint-disable-next-line no-undef
    localStorage?.getItem(REFRESH_TOKEN);
  const getCurrentUser = () =>
    // eslint-disable-next-line no-undef
    localStorage?.getItem(USER)
      ? // eslint-disable-next-line no-undef
        JSON?.parse(localStorage?.getItem(USER))
      : {};
  const isTwoFaSet = () =>
    // eslint-disable-next-line no-undef
    localStorage?.getItem(IS_TWO_FA_ENABLED);
  const fetchTwoFaKey = () =>
    // eslint-disable-next-line no-undef
    localStorage?.getItem(TWO_FA_KEY)
      ? // eslint-disable-next-line no-undef
        JSON?.parse(localStorage?.getItem(TWO_FA_KEY))
      : {};
  const getCurrentUserRole = () => {
    const loggedInUser = getLoggedInUser();
    return loggedInUser?.roles?.[0] || '';
  };

  const setProjectTopicId = (data) => {
    dispatch({ type: 'SET_TOPIC_ID', data });
  };

  const isAuthenticated = () => state?.authenticated;

  const initializeAuth = (
    authToken,
    userData,
    refreshToken,
    isTwoFaEnabled,
    twoFaObj,
  ) => {
    const token = authToken || getToken();
    const user = userData || getCurrentUser();
    const refresh = refreshToken || getRefreshToken();
    const twoFaKey = twoFaObj || false;
    const twoFaEnabled = isTwoFaEnabled || false;
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      dispatch({ type: 'SET_TOKEN', data: token });
      dispatch({ type: 'SET_REFRESH_TOKEN', data: refresh });
      dispatch({ type: 'SET_AUTHENTICATED', data: true });
      dispatch({ type: 'SET_CURRENT_USER', data: user });
      dispatch({ type: 'SET_TWO_FA_VERIFIED', data: twoFaEnabled });
      dispatch({ type: 'SET_TWO_FA_KEY', data: twoFaKey });
      if (user?.roles?.[0]) {
        dispatch({ type: 'SET_CURRENT_ROLE', data: user?.roles?.[0] });
      }
    }
  };

  const value = {
    state,
    dispatch,
    isAuthenticated,
    getToken,
    getRefreshToken,
    initializeAuth,
    getCurrentUserRole,
    getCurrentUser,
    isTwoFaSet,
    fetchTwoFaKey,
    setProjectTopicId,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

const AppContextConsumer = AppContext?.Consumer;

export { AppContext, AppContextConsumer, AppContextProvider };
